import React, { useState, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import { clsx } from 'clsx';
import InputError from '@/components/form/base/InputError.tsx';
import { ChevronDown } from 'lucide-react';
import { PhoneNumberDto } from '@/types/PhoneNumberDto.ts';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  CountryCode,
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.full.json';
import countryNamesPl from 'react-phone-input-2/lang/pl.json';
import { countryToFlag } from '@/utils/countryUtils.ts';

type CountryMetadata = { countries: Record<string, unknown[]> };

// Get max phone number length for a country
const getMaxPhoneLength = (countryCode: CountryCode): number => {
  const countryMeta = (metadata as CountryMetadata).countries[countryCode];
  if (countryMeta && Array.isArray(countryMeta[3])) {
    const lengths = countryMeta[3] as number[];
    return Math.max(...lengths);
  }
  return 15; // Fallback to international max
};

// Get country code from dial code
const getCountryFromDialCode = (dialCode: string): CountryCode => {
  const dialCodeWithoutPlus = dialCode?.replace('+', '');
  const countries = getCountries();
  for (const country of countries) {
    if (getCountryCallingCode(country) === dialCodeWithoutPlus) {
      return country;
    }
  }
  return 'PL'; // Default to Poland
};

// Prepare sorted country list with names
const prepareCountryList = () => {
  const countries = getCountries();
  const countryNames = countryNamesPl as Record<string, string>;

  return countries
    .map((code) => ({
      code,
      dialCode: getCountryCallingCode(code),
      name: countryNames[code.toLowerCase()] || code,
      flag: countryToFlag(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pl'));
};

const countryList = prepareCountryList();

// Preferred countries to show at top
const preferredCountries = ['PL', 'DE', 'GB', 'US', 'FR'];

const CacPhoneInput = ({
  fieldPath,
  error,
  label = 'Numer telefonu',
}: {
  fieldPath: string;
  error?: string;
  label?: string;
}) => {
  const { control } = useFormContext();

  const {
    field: { value, onChange },
  } = useController({
    name: fieldPath,
    control,
    defaultValue: { dialCode: '+48', number: '' } as PhoneNumberDto,
  });

  const phoneValue = value as PhoneNumberDto;

  // Track selected country separately for UI
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(() =>
    getCountryFromDialCode(phoneValue.dialCode),
  );

  // Format the number for display using AsYouType
  const formattedNumber = useMemo(() => {
    if (!phoneValue.number) return '';
    const asYouType = new AsYouType(selectedCountry);
    // input() returns the formatted string as you type
    return asYouType.input(phoneValue.number);
  }, [phoneValue.number, selectedCountry]);

  // Sorted country list with preferred countries at top
  const sortedCountryList = useMemo(() => {
    const preferred = preferredCountries
      .map((code) => countryList.find((c) => c.code === code))
      .filter(Boolean);
    const rest = countryList.filter(
      (c) => !preferredCountries.includes(c.code),
    );
    return [...preferred, ...rest];
  }, []);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCountry = e.target.value as CountryCode;
    setSelectedCountry(newCountry);
    const newDialCode = '+' + getCountryCallingCode(newCountry);
    onChange({
      dialCode: newDialCode,
      number: phoneValue.number,
    } as PhoneNumberDto);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Extract only digits from input
    const digits = e.target.value.replace(/\D/g, '');

    // Get max length for this country and limit
    const maxLength = getMaxPhoneLength(selectedCountry);
    const limitedDigits = digits.slice(0, maxLength);

    onChange({
      dialCode: '+' + getCountryCallingCode(selectedCountry),
      number: limitedDigits,
    } as PhoneNumberDto);
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <InputLabel label={label} required={true} />
      <div className="flex">
        {/* Country selector */}
        <div className="relative">
          <select
            value={selectedCountry}
            onChange={handleCountryChange}
            className={clsx(
              'h-full appearance-none rounded-l-md border border-r-0 bg-white py-1.5 pl-3 pr-8 text-sm',
              error ? 'border-error-100' : 'border-neutral-200',
            )}
          >
            {sortedCountryList.map((country) => (
              <option key={country!.code} value={country!.code}>
                {country!.flag} +{country!.dialCode}
              </option>
            ))}
          </select>
          <ChevronDown
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-600"
            size={16}
            strokeWidth={2}
          />
        </div>

        {/* Phone number input */}
        <input
          type="tel"
          autoComplete="tel-national"
          enterKeyHint="next"
          value={formattedNumber}
          onChange={handleNumberChange}
          placeholder="123 456 789"
          className={clsx(
            'w-full rounded-r-md border py-1.5 pl-3 pr-3 text-sm',
            error ? 'border-error-100' : 'border-neutral-200',
          )}
        />
      </div>
      <InputError error={error} />
    </div>
  );
};

export default CacPhoneInput;
